import { motion, AnimatePresence } from 'framer-motion'
import { FluentEmoji } from '@/components/ui/fluent-emoji'
import { useEffect } from 'react'
import { haptic } from '@/lib/native/haptics'
import { playExplosionSound } from '@/lib/native/sounds'

interface StreakExplosionProps {
  active: boolean
  targetPosition: { x: number; y: number } | null
}

export function StreakExplosion({ active, targetPosition }: StreakExplosionProps) {
  // Strong haptic feedback and sound (removed body shake as it affects all fixed elements)
  useEffect(() => {
    if (active) {
      // Strong haptic burst for streak explosion
      haptic.streak()
      // Play explosion sound
      playExplosionSound()
    }
  }, [active])
  
  if (!active || !targetPosition) return null
  
  // Generate 25+ fire particles
  const particleCount = 25
  const particles = Array.from({ length: particleCount }, (_, i) => {
    const angle = (i / particleCount) * Math.PI * 2
    const distance = 60 + Math.random() * 80
    const targetX = targetPosition.x + Math.cos(angle) * distance
    const targetY = targetPosition.y + Math.sin(angle) * distance
    const delay = Math.random() * 0.1
    const duration = 0.8 + Math.random() * 0.4
    
    return {
      id: i,
      targetX,
      targetY,
      delay,
      duration,
      emoji: Math.random() > 0.3 ? '🔥' : '✨',
      size: 16 + Math.random() * 16,
    }
  })
  
  return (
    <div className="fixed inset-0 pointer-events-none z-[200]">
      <AnimatePresence>
        {/* Radial glow pulse */}
        <motion.div
          key="streak-glow-pulse"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ 
            scale: [0, 2.5, 3],
            opacity: [0, 0.8, 0],
          }}
          exit={{ opacity: 0 }}
          transition={{ 
            duration: 1,
            ease: 'easeOut',
          }}
          className="absolute rounded-full"
          style={{
            left: targetPosition.x,
            top: targetPosition.y,
            width: 120,
            height: 120,
            marginLeft: -60,
            marginTop: -60,
            background: 'radial-gradient(circle, rgba(251, 146, 60, 0.6) 0%, rgba(249, 115, 22, 0.3) 50%, transparent 70%)',
            filter: 'blur(8px)',
          }}
        />
        
        {/* Secondary pulse ring */}
        <motion.div
          key="streak-secondary-ring"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ 
            scale: [0, 3, 4],
            opacity: [0, 0.6, 0],
          }}
          exit={{ opacity: 0 }}
          transition={{ 
            duration: 1.2,
            delay: 0.1,
            ease: 'easeOut',
          }}
          className="absolute rounded-full border-4 border-orange-400"
          style={{
            left: targetPosition.x,
            top: targetPosition.y,
            width: 100,
            height: 100,
            marginLeft: -50,
            marginTop: -50,
          }}
        />
        
        {/* Fire particles */}
        {particles.map((particle) => (
          <motion.div
            key={`streak-particle-${particle.id}`}
            initial={{
              x: targetPosition.x,
              y: targetPosition.y,
              scale: 0,
              opacity: 0,
              rotate: 0,
            }}
            animate={{
              x: particle.targetX,
              y: particle.targetY,
              scale: [0, 1.5, 1, 0],
              opacity: [0, 1, 1, 0],
              rotate: [0, Math.random() * 360],
            }}
            transition={{
              duration: particle.duration,
              delay: particle.delay,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
            exit={{ opacity: 0, scale: 0 }}
            className="absolute -translate-x-1/2 -translate-y-1/2"
          >
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, 10, -10, 0],
              }}
              transition={{
                duration: 0.3,
                repeat: 2,
                ease: 'easeInOut',
              }}
            >
              <FluentEmoji emoji={particle.emoji} size={particle.size} animated />
            </motion.div>
          </motion.div>
        ))}
        
        {/* Central burst flash */}
        <motion.div
          key="streak-burst-flash"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ 
            scale: [0, 1.5, 2],
            opacity: [0, 1, 0],
          }}
          transition={{ 
            duration: 0.5,
            ease: 'easeOut',
          }}
          className="absolute rounded-full bg-orange-400"
          style={{
            left: targetPosition.x,
            top: targetPosition.y,
            width: 80,
            height: 80,
            marginLeft: -40,
            marginTop: -40,
            filter: 'blur(20px)',
          }}
        />
        
        {/* Expanding fire rings */}
        {[0, 1, 2].map((i) => (
          <motion.div
            key={`streak-ring-${i}`}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ 
              scale: [0, 2 + i * 0.5],
              opacity: [0, 0.7, 0],
            }}
            transition={{ 
              duration: 0.8,
              delay: i * 0.1,
              ease: 'easeOut',
            }}
            className="absolute rounded-full border-2"
            style={{
              left: targetPosition.x,
              top: targetPosition.y,
              width: 60,
              height: 60,
              marginLeft: -30,
              marginTop: -30,
              borderColor: i === 0 ? '#fb923c' : i === 1 ? '#f97316' : '#ea580c',
            }}
          />
        ))}
        
        {/* Floating streak text - positioned below and to the left of explosion */}
        <div
          key="streak-text-wrapper"
          className="absolute"
          style={{
            left: targetPosition.x - 60,
            top: targetPosition.y + 80,
            transform: 'translate(-50%, -50%)',
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.5 }}
            animate={{ 
              opacity: [0, 1, 1, 0],
              y: [20, 0],
              scale: [0.5, 1.2, 1, 0.8],
            }}
            transition={{ 
              duration: 1.5,
              ease: 'easeOut',
            }}
          >
            <div className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 px-4 py-2 rounded-full shadow-2xl whitespace-nowrap">
              <FluentEmoji emoji="🔥" size={24} animated />
              <span className="text-xl font-black text-white">STREAK!</span>
            </div>
          </motion.div>
        </div>
      </AnimatePresence>
    </div>
  )
}
